import {
  Box,
  Table as MuiTable,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  keyframes,
  styled,
  useTheme,
} from "@mui/material";

import EmptyState from "./EmptyState";
import { EmptyStateIcon } from "assets/Icon";
import useIsMobileView from "hooks/useIsMobileView";
import { useMemo } from "react";
import { useState } from "react";

export function Table({
  headers,
  items,
  hideBorders = false,
  onRowClick = () => { },
  dense = false,
  onSort,
  isRowBlink = false
}) {
  const theme = useTheme();
  const isMobileView = useIsMobileView();

  const [sortedColumn, setSortedColumn] = useState(null);

  const handleClickColumn = ({ key, sortable, sortKey }) => {
    if (sortable) {
      if (key === sortedColumn?.key) {
        if (sortedColumn.sort === "asc") {
          setSortedColumn({ key: key, sort: "desc", sortKey });

          if (onSort) {
            onSort({ key: key, sort: "desc", sortKey });
          }
        } else {
          setSortedColumn(null);

          if (onSort) {
            onSort(null);
          }
        }
      } else {
        setSortedColumn({ key: key, sort: "asc", sortKey });

        if (onSort) {
          onSort({ key: key, sort: "asc", sortKey });
        }
      }
    }
  };

  const sortedValue = useMemo(() => {
    const itemsToSort = items ? [...items] : [];

    if (sortedColumn) {
      if (sortedColumn.sort === "asc") {
        return itemsToSort.sort((a, b) => {
          if (a.data[sortedColumn.sortKey] < b.data[sortedColumn.sortKey]) {
            return 1;
          }
          if (a.data[sortedColumn.sortKey] > b.data[sortedColumn.sortKey]) {
            return -1;
          }
          return 0;
        });
      } else {
        return itemsToSort.sort((a, b) => {
          if (a.data[sortedColumn.sortKey] < b.data[sortedColumn.sortKey]) {
            return -1;
          }
          if (a.data[sortedColumn.sortKey] > b.data[sortedColumn.sortKey]) {
            return 1;
          }
          return 0;
        });
      }
    } else {
      return items;
    }
  }, [sortedColumn, items]);

  return (
    <TableContainerStyle component={Paper}>
      <MuiTableStyle hideBorders={hideBorders}>
        <TableHeadStyle dense={dense}>
          <TableRow>
            {headers?.map((headCell) => (
              <TableHeadCellStyle
                dense={dense}
                key={headCell.key}
                align={headCell.align}
                width={headCell?.width}
                onClick={() => handleClickColumn(headCell)}
                sortable={headCell?.sortable}
              >
                <HeadCellIconWrapper
                  display={headCell?.display || "inline-block"}
                  sortable={headCell?.sortable}
                  selected={sortedColumn && sortedColumn.key === headCell.key}
                >
                  {!headCell?.hideSortIcon && (
                    <>
                      {isMobileView ? (
                        <ResponsiveSortIcons sort={sortedColumn?.sort}>
                          <ion-icon
                            className="up-icon"
                            name="chevron-up-outline"
                          ></ion-icon>

                          <ion-icon
                            className="down-icon"
                            name="chevron-down-outline"
                          ></ion-icon>
                        </ResponsiveSortIcons>
                      ) : (
                        <div className="sort-icon">
                          <ion-icon
                            name={
                              sortedColumn?.sort === "asc"
                                ? "arrow-down-outline"
                                : "arrow-up-outline"
                            }
                          ></ion-icon>
                        </div>
                      )}
                    </>
                  )}

                  {headCell.name}
                </HeadCellIconWrapper>
              </TableHeadCellStyle>
            ))}
          </TableRow>
        </TableHeadStyle>

        <TableBody>
          {sortedValue?.length > 0 ? (
            sortedValue?.map((row, index) => (
              <TableRowStyle
                color={row?.color}
                onClick={() => onRowClick && onRowClick(row)}
                key={index}
                isRowBlink={isRowBlink}
              >
                {headers?.map((headCell) => (
                  <TableCellStyle
                    dense={dense}
                    key={`${index}-${headCell.key}`}
                    align={headCell.align}
                  >
                    {row[headCell?.key]}
                  </TableCellStyle>
                ))}
              </TableRowStyle>
            ))
          ) : (
            <TableRowStyle>
              <TableCellStyle colSpan={headers?.length}>
                <EmptyState
                  text={"No Data Available"}
                  icon={<EmptyStateIcon color={theme.palette.modules.border} />}
                />
              </TableCellStyle>
            </TableRowStyle>
          )}
        </TableBody>
      </MuiTableStyle>
    </TableContainerStyle>
  );
}

const blink = (color) => keyframes`
  0% {
    background: inherit;
  }
  50% {
    background: ${color};
  }
  100% {
    background: inherit;
  }
`;

export const TableContainerStyle = styled(TableContainer)({
  width: "100%",
  overflow: "auto",
});

export const TableCellStyle = styled(TableCell)(({ theme, dense }) => ({
  padding: dense ? theme.spacing(0.8, 0.5) : theme.spacing(2),

  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(1.5, 0.5),
  },
}));

export const HeadCellIconWrapper = styled(Box)(
  ({ sortable, selected, theme, display }) => ({
    position: "relative",
    display: display,

    ".sort-icon ion-icon": {
      transition: "0.3s",
      opacity: selected ? 1 : 0,
      position: "absolute",
      right: "-15px",
      top: "calc(50% - 2px)",
      transform: "translateY(-50%)",

      [theme.breakpoints.down("md")]: {
        opacity: 1,
      },
    },

    "&:hover": {
      ".sort-icon ion-icon": { opacity: sortable ? 0.6 : 0 },
    },
  })
);

export const TableHeadCellStyle = styled(TableCell)(
  ({ theme, dense, sortable }) => ({
    position: "relative",
    cursor: sortable ? "pointer" : "default",
    padding: dense ? theme.spacing(0.8, 0.5) : theme.spacing(2),
    color: `${theme.palette.text.primary} !important`,

    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(1.5, 0.5),
    },
  })
);

export const MuiTableStyle = styled(MuiTable)(({ hideBorders, theme }) => ({
  border: hideBorders ? "none" : "rgba(172, 177, 180, 0.2)",

  [theme.breakpoints.down("md")]: {
    border: "none",
  },
}));

export const TableHeadStyle = styled(TableHead)(({ theme, dense }) => ({
  background: dense ? "transparent !important" : "",

  [theme.breakpoints.down("md")]: {
    background: "none !important",
  },
}));

export const TableRowStyle = styled(TableRow, {
  shouldForwardProp: (props) => !['color', 'onRowClick', 'isRowBlink'].includes(props)
})(({ color, onRowClick, isRowBlink }) => ({
  background: color,
  cursor: onRowClick ? "pointer" : "text",

  animation: (color && isRowBlink) ? `${blink(color)} 0.8s ease-in 5 forwards` : "none",
}));

export const ResponsiveSortIcons = styled(Box)(({ sort, theme }) => ({
  position: "absolute",
  right: "-15px",
  top: "calc(50% - 2px)",
  transform: "translateY(-50%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",

  "ion-icon": {
    width: "10px",
    height: "10px",

    "&.up-icon": {
      color:
        sort === "asc"
          ? theme.palette.secondary.main
          : theme.palette.text.secondary,

      width: sort === "asc" ? "13px" : "10px",
      height: sort === "asc" ? "13px" : "10px",
    },

    "&.down-icon": {
      color:
        sort === "desc"
          ? theme.palette.secondary.main
          : theme.palette.text.secondary,
      width: sort === "desc" ? "13px" : "10px",
      height: sort === "desc" ? "13px" : "10px",
    },
  },
}));

export default Table;
